import React, { useEffect, useState } from "react";
import { register, login, logout, onAuthChange } from "../services/auth.service.js";
import { Link } from "react-router-dom";
import { getUserProfile, updateUserProfile, saveUserAddress } from "../services/user.service.js";

const Account = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Register form state
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regName, setRegName] = useState("");

  // Edit Profile state
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");

  // Address form state
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    fullName: "",
    phone: "",
    addressLine1: "",
    city: "",
    state: "",
    zipCode: ""
  });

  const [setFormError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [regError, setRegError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userProfile = await getUserProfile(currentUser.uid);
        if (userProfile) {
          setProfile(userProfile);
          setEditName(userProfile.name || currentUser.displayName || "");
          setEditPhone(userProfile.phone || "");
        } else {
            setEditName(currentUser.displayName || "");
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setRegError("");
    setIsSubmitting(true);
    try {
      await login(loginEmail, loginPassword);
    } catch (error) {
      console.error("Login error:", error);
      let message = "Login failed. Please check your credentials.";
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = "Invalid email or password.";
      } else if (error.code === 'auth/too-many-requests') {
        message = "Too many failed attempts. Please try again later.";
      }
      setLoginError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError("");
    setLoginError("");
    
    // Basic Validation
    if (regName.trim().length < 2) {
      setRegError("Please enter your full name.");
      return;
    }
    if (regPassword.length < 6) {
      setRegError("Password must be at least 6 characters long.");
      return;
    }

    setIsSubmitting(true);
    console.log("Starting registration for:", regEmail);
    try {
      const userCredential = await register(regEmail, regPassword);
      const authUser = userCredential.user;
      console.log("Auth user created:", authUser.uid);
      
      const profileData = {
          name: regName,
          email: regEmail,
          role: "USER",
          createdAt: new Date().toISOString()
      };
      
      console.log("Saving profile data to Firestore:", profileData);
      await updateUserProfile(authUser.uid, profileData);
      console.log("Profile data saved successfully");
      
    } catch (error) {
      console.error("Registration error:", error);
      let message = "Registration failed. Please try again.";
      if (error.code === 'auth/email-already-in-use') {
        message = "This email is already registered.";
      } else if (error.code === 'auth/invalid-email') {
        message = "Please enter a valid email address.";
      } else if (error.code === 'auth/weak-password') {
        message = "Password is too weak.";
      }
      setRegError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (editName.trim().length < 2) {
      alert("Name must be at least 2 characters.");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUserProfile(user.uid, {
        name: editName,
        phone: editPhone
      });
      setProfile({ ...profile, name: editName, phone: editPhone });
      alert("Profile updated successfully!");
    } catch (error) {
      console.error("Update error:", error);
      alert("Update failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const updatedAddresses = await saveUserAddress(user.uid, newAddress);
      setProfile({ ...profile, addresses: updatedAddresses });
      setShowAddressForm(false);
      setNewAddress({
        fullName: "",
        phone: "",
        addressLine1: "",
        city: "",
        state: "",
        zipCode: ""
      });
      alert("Address added successfully!");
    } catch (error) {
      alert("Failed to add address: " + error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  if (loading) return <div className="py-80 text-center">Loading...</div>;

  if (user) {
    return (
      <section className='account py-80'>
        <div className='container container-lg'>
          <div className="row gy-4">
            <div className="col-lg-4">
              <div className="border border-gray-100 rounded-16 p-32 bg-white shadow-sm sticky-top" style={{ top: '100px', zIndex: 10 }}>
                <div className="d-flex align-items-center gap-24 mb-32 p-16 bg-gray-50 rounded-16">
                  <div className="w-64 h-64 bg-main-600 rounded-circle flex-center text-white text-2xl fw-bold">
                    {profile?.name?.charAt(0) || user.email?.charAt(0)}
                  </div>
                  <div>
                    <h6 className="mb-4 fw-bold">{profile?.name || user.displayName || "User"}</h6>
                    <span className="text-sm text-gray-500 d-block">{user.email}</span>
                  </div>
                </div>
                
                <div className="list-group list-group-flush mb-32">
                  <button 
                    onClick={() => setActiveTab("profile")}
                    className={`list-group-item list-group-item-action py-16 border-0 d-flex align-items-center gap-12 rounded-8 mb-8 ${activeTab === "profile" ? "bg-main-50 text-main-600 fw-bold" : "hover-bg-gray-50"}`}
                  >
                    <i className="ph ph-user text-2xl" />
                    <span>My Profile</span>
                  </button>
                  <button 
                    onClick={() => setActiveTab("addresses")}
                    className={`list-group-item list-group-item-action py-16 border-0 d-flex align-items-center gap-12 rounded-8 mb-8 ${activeTab === "addresses" ? "bg-main-50 text-main-600 fw-bold" : "hover-bg-gray-50"}`}
                  >
                    <i className="ph ph-map-pin text-2xl" />
                    <span>Manage Addresses</span>
                  </button>
                  <Link to="/orders" className="list-group-item list-group-item-action py-16 border-0 d-flex align-items-center gap-12 rounded-8 mb-8 hover-bg-gray-50">
                    <i className="ph ph-package text-2xl text-main-600" />
                    <span className="fw-bold text-gray-800">My Orders</span>
                    <i className="ph ph-caret-right ms-auto text-gray-400" />
                  </Link>
                </div>

                <button onClick={handleLogout} className="btn bg-danger-600 text-white w-100 py-14 fw-bold rounded-12 transition-2 hover-bg-danger-700">
                  Sign Out
                </button>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="border border-gray-100 rounded-16 p-32 bg-white shadow-sm">
                {activeTab === "profile" && (
                  <div className="profile-tab">
                    <h5 className="mb-32 fw-bold">Edit Profile</h5>
                    <form onSubmit={handleUpdateProfile}>
                      <div className="row gy-4">
                        <div className="col-sm-6">
                          <label className="text-neutral-900 text-lg mb-8 fw-medium">Full Name</label>
                          <input 
                            type="text" 
                            className="common-input" 
                            placeholder="Enter Name"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label className="text-neutral-900 text-lg mb-8 fw-medium">Phone Number</label>
                          <input 
                            type="text" 
                            className="common-input" 
                            placeholder="Enter Phone"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                          />
                        </div>
                        <div className="col-12">
                          <label className="text-neutral-900 text-lg mb-8 fw-medium">Email Address</label>
                          <input type="email" className="common-input bg-gray-50" value={user.email} readOnly disabled />
                          <span className="text-xs text-gray-400 mt-4 d-block">Email cannot be changed</span>
                        </div>
                        <div className="col-12 mt-32">
                          <button type="submit" className="btn btn-main py-16 px-40 rounded-12 fw-bold">Save Changes</button>
                        </div>
                      </div>
                    </form>
                  </div>
                )}

                {activeTab === "addresses" && (
                  <div className="addresses-tab">
                    <div className="flex-between gap-16 mb-32 flex-wrap">
                      <h5 className="mb-0 fw-bold">My Addresses</h5>
                      <button 
                        onClick={() => setShowAddressForm(!showAddressForm)}
                        className="btn btn-outline-main py-10 px-24 rounded-12 fw-bold flex-align gap-8"
                      >
                        <i className={showAddressForm ? "ph ph-minus" : "ph ph-plus"} />
                        {showAddressForm ? "Cancel" : "Add New Address"}
                      </button>
                    </div>

                    {showAddressForm && (
                      <div className="bg-gray-50 p-24 rounded-16 mb-40 border border-gray-100">
                        <h6 className="mb-24">New Address</h6>
                        <form onSubmit={handleAddAddress}>
                          <div className="row gy-3">
                            <div className="col-sm-6">
                              <label className="text-sm fw-bold mb-4">Receiver Name</label>
                              <input 
                                type="text" 
                                className="common-input" 
                                required
                                value={newAddress.fullName}
                                onChange={(e) => setNewAddress({...newAddress, fullName: e.target.value})}
                              />
                            </div>
                            <div className="col-sm-6">
                              <label className="text-sm fw-bold mb-4">Phone Number</label>
                              <input 
                                type="text" 
                                className="common-input" 
                                required
                                value={newAddress.phone}
                                onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                              />
                            </div>
                            <div className="col-12">
                              <label className="text-sm fw-bold mb-4">Address Line</label>
                              <input 
                                type="text" 
                                className="common-input" 
                                required
                                value={newAddress.addressLine1}
                                onChange={(e) => setNewAddress({...newAddress, addressLine1: e.target.value})}
                              />
                            </div>
                            <div className="col-sm-4">
                              <label className="text-sm fw-bold mb-4">City</label>
                              <input 
                                type="text" 
                                className="common-input" 
                                required
                                value={newAddress.city}
                                onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                              />
                            </div>
                            <div className="col-sm-4">
                              <label className="text-sm fw-bold mb-4">State</label>
                              <input 
                                type="text" 
                                className="common-input" 
                                required
                                value={newAddress.state}
                                onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                              />
                            </div>
                            <div className="col-sm-4">
                              <label className="text-sm fw-bold mb-4">Zip Code</label>
                              <input 
                                type="text" 
                                className="common-input" 
                                required
                                value={newAddress.zipCode}
                                onChange={(e) => setNewAddress({...newAddress, zipCode: e.target.value})}
                              />
                            </div>
                            <div className="col-12 mt-24">
                              <button type="submit" className="btn btn-main w-100 py-14 rounded-12 fw-bold">Save Address</button>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="row gy-4">
                      {profile?.addresses && profile.addresses.length > 0 ? (
                        profile.addresses.map((addr) => (
                          <div key={addr.id} className="col-md-6">
                            <div className="border border-gray-100 p-24 rounded-16 hover-border-main-600 transition-1 h-100 position-relative">
                              <span className="badge bg-main-600 text-white position-absolute top-24 end-24 px-12 py-6 rounded-pill text-xs">Home</span>
                              <h6 className="mb-12 fw-bold">{addr.fullName}</h6>
                              <p className="text-gray-600 text-sm mb-16">{addr.addressLine1}, {addr.city}, {addr.state} - {addr.zipCode}</p>
                              <div className="d-flex align-items-center gap-8 text-gray-800 fw-bold">
                                <i className="ph ph-phone" />
                                <span>{addr.phone}</span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-12 text-center py-40 bg-gray-50 rounded-16">
                          <i className="ph ph-map-pin text-4xl text-gray-300 mb-8" />
                          <p className="text-gray-500 mb-0">No saved addresses found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='account py-80'>
      <div className='container container-lg'>
        <div className='row gy-4'>
          {/* Login Card Start */}
          <div className='col-xl-6 pe-xl-5'>
            <div className='border border-gray-100 hover-border-main-600 transition-1 rounded-16 px-24 py-40 h-100'>
              <h6 className='text-xl mb-32'>Login</h6>
              {loginError && (
                <div className="alert alert-danger mb-24 rounded-12 text-sm py-12 px-16 border-0 bg-danger-50 text-danger-600 fw-medium">
                  <i className="ph ph-warning-circle me-8" /> {loginError}
                </div>
              )}
              <form onSubmit={handleLogin}>
                <div className='mb-24'>
                  <label
                    htmlFor='loginEmail'
                    className='text-neutral-900 text-lg mb-8 fw-medium'
                  >
                    Email address <span className='text-danger'>*</span>{" "}
                  </label>
                  <input
                    type='email'
                    className='common-input'
                    id='loginEmail'
                    placeholder='Enter Email'
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
                <div className='mb-24'>
                  <label
                    htmlFor='loginPassword'
                    className='text-neutral-900 text-lg mb-8 fw-medium'
                  >
                    Password <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='password'
                    className='common-input'
                    id='loginPassword'
                    placeholder='Enter Password'
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                </div>
                <div className='mb-24 mt-48'>
                  <button 
                    type='submit' 
                    className='btn btn-main py-18 px-40 rounded-12 fw-bold'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Please wait..." : "Log in"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Login Card End */}
          
          {/* Register Card Start */}
          <div className='col-xl-6'>
            <div className='border border-gray-100 hover-border-main-600 transition-1 rounded-16 px-24 py-40'>
              <h6 className='text-xl mb-32'>Register</h6>
              {regError && (
                <div className="alert alert-danger mb-24 rounded-12 text-sm py-12 px-16 border-0 bg-danger-50 text-danger-600 fw-medium">
                  <i className="ph ph-warning-circle me-8" /> {regError}
                </div>
              )}
              <form onSubmit={handleRegister}>
                <div className='mb-24'>
                  <label
                    htmlFor='regName'
                    className='text-neutral-900 text-lg mb-8 fw-medium'
                  >
                    Full Name <span className='text-danger'>*</span>{" "}
                  </label>
                  <input
                    type='text'
                    className='common-input'
                    id='regName'
                    placeholder='Write your name'
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    required
                  />
                </div>
                <div className='mb-24'>
                  <label
                    htmlFor='regEmail'
                    className='text-neutral-900 text-lg mb-8 fw-medium'
                  >
                    Email address <span className='text-danger'>*</span>{" "}
                  </label>
                  <input
                    type='email'
                    className='common-input'
                    id='regEmail'
                    placeholder='Enter Email Address'
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    required
                  />
                </div>
                <div className='mb-24'>
                  <label
                    htmlFor='regPassword'
                    className='text-neutral-900 text-lg mb-8 fw-medium'
                  >
                    Password <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='password'
                    className='common-input'
                    id='regPassword'
                    placeholder='Enter Password'
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    required
                  />
                </div>
                <div className='mt-48'>
                  <button 
                    type='submit' 
                    className='btn btn-main py-18 px-40 rounded-12 fw-bold'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Please wait..." : "Register"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Register Card End */}
        </div>
      </div>
    </section>
  );
};

export default Account;